import { normalize } from 'normalizr';
import { QueryConfig } from 'redux-query';
import { patient, chart as chartSchema } from 'modules/schema';

import {
  PatientLoginInfo,
  LoginSuccessResponse,
  PatientsState,
  ChartsState,
  PatientChartInfo,
  MutateQuery,
} from 'types';
import { PatientQueryKeys } from './constants';

export const loginQuery = (loginInfo: PatientLoginInfo, practiceId: number) => ({
  body: { ...loginInfo, practiceId },
  options: {
    method: 'POST',
  },
  queryKey: PatientQueryKeys.PatientLogin,
  transform: ({ data }: LoginSuccessResponse) => normalize(data, patient).entities,
  // USer might login in the middle of the flow, when they already entered some demographic data.
  // In that case we do not want to lose this information, that is why we merge received data
  // with data in store.
  update: {
    charts: (prevCharts: ChartsState, nextCharts: ChartsState): ChartsState => ({
      ...prevCharts,
      ...nextCharts,
    }),
    patients: (prevPatients: PatientsState, nextPatients: PatientsState): PatientsState => ({
      ...prevPatients,
      ...nextPatients,
    }),
  },
  url: 'book/api/patients/login',
});

/**
 * Returns a user for the current active session.
 * @param practiceId - id of the current practice. It is used on the backend to get a correct list
 * of charts for the patient
 */
export const getCurrentPatient = (practiceId: string | number) => ({
  body: { practiceId },
  force: true,
  transform: ({ data }: LoginSuccessResponse) => normalize(data, patient).entities,
  update: {
    charts: (_prevCharts: ChartsState, nextCharts: ChartsState): ChartsState => nextCharts,
    patients: (_prevPatients: PatientsState, nextPatients: PatientsState): PatientsState =>
      nextPatients,
  },
  url: 'book/api/patients/get_current_user',
});

export const logoutQuery = () => ({
  options: {
    method: 'POST',
  },
  queryKey: PatientQueryKeys.PatientLogout,
  url: 'book/api/patients/logout',
});

export const resetPasswordQuery = (email: string): QueryConfig => ({
  body: { email },
  queryKey: PatientQueryKeys.PatientPasswordReset,
  url: 'api/passport/password/reset/',
});

/**
 * Creates a new chart and associates it to the current user
 */
export const createChart = (
  chart: Partial<PatientChartInfo>,
  practiceData: { practiceId: number; physicianId: number },
): MutateQuery => ({
  body: { ...practiceData, chart },
  transform: ({ data }): { charts: ChartsState } => normalize(data, chartSchema).entities,
  update: {
    charts: (prevCharts: ChartsState, nextCharts: ChartsState): ChartsState => ({
      ...prevCharts,
      ...nextCharts,
    }),
  },
  url: 'book/api/patients',
});
